// SEO + AIEO data, single source of truth.
//
// Used by:
//   - scripts/prerender-static.mjs   (compile-time, per-route HTML)
//   - src/main.jsx                   (runtime, hash-route changes)
//   - vite.config.js                 (build-time JSON-LD injection into index.html)
//
// Keep this file ESM-only and free of React/DOM imports so Node scripts
// can `import` it without a bundler.

export const SITE = {
  origin: 'https://crayo.ro',
  name: 'Crayo',
  legalName: 'SHIPDRONES SRL',
  locale: 'ro_RO',
  defaultLang: 'ro',
  email: 'salut@crayo.ro',
  ogImage: 'https://crayo.ro/og-image.png',
};

// Per-route meta. Keys are pathname (without trailing slash, except '/').
// description is plain text (no HTML), <=155 chars for SERP truncation.
export const ROUTES = {
  '/': {
    title: 'Crayo · Cartea ta de colorat, din pozele voastre',
    description: 'Încarci pozele copilului tău și primești cartea ta de colorat în PDF în 2 minute. 9,90 lei în loc de 19,90 (-50%), garanție 100% banii înapoi.',
    canonical: 'https://crayo.ro/',
    type: 'website',
  },
  '/despre': {
    title: 'Despre Crayo · Făcut de părinți, pentru copii',
    description: 'De ce am construit Crayo, cum funcționează în spate și ce promitem în privința pozelor copiilor voștri.',
    canonical: 'https://crayo.ro/despre',
    type: 'website',
  },
  '/contact': {
    title: 'Contact · Crayo',
    description: 'Scrie-ne la salut@crayo.ro. Răspundem în maxim 48 de ore.',
    canonical: 'https://crayo.ro/contact',
    type: 'website',
  },
  '/termeni': {
    title: 'Termeni și condiții · Crayo',
    description: 'Termenii contractuali pentru utilizarea serviciului Crayo, operat de SHIPDRONES SRL.',
    canonical: 'https://crayo.ro/termeni',
    type: 'website',
  },
  '/confidentialitate': {
    title: 'Politica de confidențialitate · Crayo',
    description: 'Cum prelucrăm datele tale: ce colectăm, cu cine partajăm, cât păstrăm. Conform GDPR.',
    canonical: 'https://crayo.ro/confidentialitate',
    type: 'website',
  },
  '/cookies': {
    title: 'Politica de cookies · Crayo',
    description: 'Cookies strict necesare și opționale, cum le dezactivezi.',
    canonical: 'https://crayo.ro/cookies',
    type: 'website',
  },
  // Dynamic in-app routes — noindex (see NOINDEX_PATHS below). Titles
  // exist purely so GA4 funnel reports can group page_views by title
  // instead of all inheriting the homepage title. Description is
  // inherited at build time only as a fallback for crawlers; the
  // real-time meta-update path on hashchange picks up the title.
  '/create': {
    title: 'Încarcă pozele · Crayo',
    description: 'Încarcă pozele copilului tău și primești previzualizarea gratuit.',
    canonical: 'https://crayo.ro/',
    type: 'website',
  },
  '/preview': {
    title: 'Previzualizare · Crayo',
    description: 'Previzualizarea cărții tale de colorat.',
    canonical: 'https://crayo.ro/',
    type: 'website',
  },
  '/checkout': {
    title: 'Plată · Crayo',
    description: 'Finalizează cumpărarea cărții tale de colorat.',
    canonical: 'https://crayo.ro/',
    type: 'website',
  },
  '/download': {
    title: 'Cartea ta · Crayo',
    description: 'Descarcă PDF-ul cărții tale de colorat.',
    canonical: 'https://crayo.ro/',
    type: 'website',
  },
};

// Routes that must NEVER be indexed (dynamic flows that include user data
// or one-shot tokens). The hash router serves these only via fragments
// today, but we set robots=noindex defensively at runtime.
export const NOINDEX_PATHS = ['/create', '/preview', '/checkout', '/download'];

// JSON-LD payloads. Embedded as <script type="application/ld+json"> blocks
// in every rendered HTML file so AI crawlers (which value structured data
// heavily) and Google's rich-results parser can both consume them.

export const ORG_JSONLD = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'Crayo',
  legalName: 'SHIPDRONES SRL',
  url: 'https://crayo.ro',
  logo: 'https://crayo.ro/og-image.png',
  email: 'salut@crayo.ro',
  address: {
    '@type': 'PostalAddress',
    streetAddress: 'Str. Marin Preda nr. 5',
    addressLocality: 'Alba Iulia',
    addressRegion: 'Alba',
    addressCountry: 'RO',
  },
  identifier: [
    { '@type': 'PropertyValue', propertyID: 'CUI', value: '46378099' },
    { '@type': 'PropertyValue', propertyID: 'RegCom', value: 'J01/831/2022' },
  ],
};

export const WEBSITE_JSONLD = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: 'Crayo',
  url: 'https://crayo.ro',
  inLanguage: 'ro-RO',
  publisher: { '@id': 'https://crayo.ro/#org' },
};

export const PRODUCT_JSONLD = {
  '@context': 'https://schema.org',
  '@type': 'Product',
  name: 'Crayo, carte de colorat personalizată',
  description: 'Pagini de colorat alb-negru generate din pozele tale, livrate ca PDF gata de printat.',
  brand: { '@type': 'Brand', name: 'Crayo' },
  image: 'https://crayo.ro/og-image.png',
  // Single flat offer — matches the post-2026-05-07 trap-fix pricing
  // (up to 20 pages per PDF, 9.90 RON). The visible page also shows a
  // 19,90 RON anchor with strikethrough; that's HTML copy and not encoded
  // here because schema.org has no clean primitive for "was X, now Y".
  offers: [
    {
      '@type': 'Offer',
      name: 'PDF carte de colorat personalizată',
      price: '9.90',
      priceCurrency: 'RON',
      availability: 'https://schema.org/InStock',
      url: 'https://crayo.ro/',
    },
  ],
};

// FAQ content is duplicated from components/Landing.jsx (FAQ() items).
// They MUST stay in sync — if you change the FAQ in the landing page,
// update this list too.
export const FAQ_JSONLD = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'Ce se întâmplă cu pozele mele după ce le încarc?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Pozele originale sunt șterse automat după 72 de ore de pe serverele noastre. PDF-ul generat rămâne al tău, îl descarci din link-ul primit pe email timp de 30 de zile. Nu folosim pozele pentru altceva decât pentru a genera pagina ta de colorat. Nu le publicăm, nu le antrenăm pe modele, nu le vindem niciodată.',
      },
    },
    {
      '@type': 'Question',
      name: 'Pot să printez paginile acasă?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Da, sunt optimizate pentru hârtie A4, alb-negru, și merg pe orice imprimantă obișnuită. Recomandăm 90 g/m² sau mai groasă, mai ales dacă vrei să colorezi cu creioane cerate sau acuarele.',
      },
    },
    {
      '@type': 'Question',
      name: 'Ce fac dacă nu-mi place rezultatul?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Prima pagină e gratuită, exact ca să vezi calitatea înainte să plătești. Iar dacă după cumpărare tot nu ești mulțumit, scrie-ne la salut@crayo.ro în maxim 14 zile și îți returnăm integral suma plătită.',
      },
    },
    {
      '@type': 'Question',
      name: 'Merge și pentru poze cu animale de companie?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Da, orice subiect clar vizibil în poză funcționează, câini, pisici, iepurași, broaște țestoase. Pozele făcute pe lumină naturală, cu subiectul în prim-plan, dau cele mai bune rezultate.',
      },
    },
    {
      '@type': 'Question',
      name: 'Când o să fie disponibilă varianta tipărită și livrată?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Lucrăm la ea, lasă-ne emailul în formularul de mai jos și te anunțăm primul. Varianta tipărită va veni legată ca o cărticică, pe hârtie groasă, livrată prin curier în România.',
      },
    },
    {
      '@type': 'Question',
      name: 'Pot primi un pachet personalizat?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Da. Pentru cazuri speciale (cadouri mai mari, evenimente, pachete cu mai multe teme), scrie-ne la salut@crayo.ro și facem unul pe măsură.',
      },
    },
  ],
};

// Build the full JSON-LD graph for embedding. Using @graph keeps each
// entity addressable by @id and lets parsers consume all in one script tag.
export function buildJsonLd({ includeFaq = false, includeProduct = false } = {}) {
  const graph = [
    { ...ORG_JSONLD, '@id': 'https://crayo.ro/#org' },
    { ...WEBSITE_JSONLD, '@id': 'https://crayo.ro/#website' },
  ];
  if (includeProduct) graph.push({ ...PRODUCT_JSONLD, '@id': 'https://crayo.ro/#product' });
  if (includeFaq) graph.push({ ...FAQ_JSONLD, '@id': 'https://crayo.ro/#faq' });
  return { '@context': 'https://schema.org', '@graph': graph };
}

// Apply meta to the live document. Called from main.jsx on hash change.
export function applyRouteMeta(path) {
  if (typeof document === 'undefined') return;
  const route = ROUTES[path] || ROUTES['/'];
  document.title = route.title;
  setMeta('description', route.description);
  setLink('canonical', route.canonical);
  setMeta('og:title', route.title, true);
  setMeta('og:description', route.description, true);
  setMeta('og:url', route.canonical, true);
  setMeta('twitter:title', route.title);
  setMeta('twitter:description', route.description);

  const isNoindex = NOINDEX_PATHS.some((p) => path === p || path.startsWith(p + '/'));
  setMeta('robots', isNoindex ? 'noindex, nofollow' : 'index, follow');
}

function setMeta(name, content, isProperty = false) {
  const attr = isProperty || name.startsWith('og:') ? 'property' : 'name';
  let el = document.head.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function setLink(rel, href) {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}
